/**
 * Plain-language definitions shown on the reports (requirements §4.6: every
 * handoff decision is stated on the report itself). Keep these in step with
 * the SQL/view definitions — they are the user-facing contract.
 */
export interface Definition {
  term: string;
  body: string;
}

const REFRESH =
  "Updated from LeadPerfection six times a day (5, 6, 7, 8 and 9 AM, and 4 PM Central). Register to Win " +
  "leads are excluded from every report row and total; they are only counted in the Register to Win tiles.";

const DEMO_PAY_RULE =
  "Demo pay rate × each lead that reached a demo, whether or not it sold. \"Not Demoed – Multi Visit\" does " +
  "not count, and totals will not match a count of LeadPerfection's Sat field. It is counted per lead: " +
  "LeadPerfection records one lead per product, so a visit with Windows and Bath leads earns demo pay twice.";

const COMMISSION_RULE =
  "Commission rate × the original sale price. Change orders don't change it: a $10,000 sale with a later " +
  "$2,000 change order earns commission on $10,000 (revenue for that sale shows $12,000). A sale " +
  "cancelled within 3 days of the sale date earns no commission.";

const MANAGER_RULE =
  "The event manager named on the Rates page earns the manager override % on every event sale (original sale " +
  "price, same 3-day cancellation rule). On leads where she is the promoter she also earns the regular " +
  "commission, so 2% + 1% = 3% on her own sales. When the role changes hands a new rate row names the new " +
  "manager, so each sale pays whoever was manager on its appointment date. The override can be hidden with " +
  "\"Show manager override\" when reviewing a rep's numbers with them.";

export const REPORT1_DEFINITIONS: Definition[] = [
  { term: "Commission", body: COMMISSION_RULE },
  { term: "Manager Override", body: MANAGER_RULE },
  {
    term: "Hours Worked and Hours per Demo",
    body:
      "Hours from LeadPerfection Event Hours (recorded from December 2024), filtered by the dates, Event and " +
      "Event Rep chosen above. Hours aren't recorded by product or result, so the Interested In and Result " +
      "filters don't change them. Hours per Demo = Hours Worked ÷ Demos.",
  },
  {
    term: "Register to Win and Hours per Register to Win",
    body:
      "Register to Win leads entered in LeadPerfection (\"Register To Win - <event>\" subsources), counted by " +
      "the date they were entered and matched to their event for the Event filter (automatically by name, or " +
      "as linked on the Register to Win tab). They have no rep, product " +
      "or result, so both tiles show 0 when Event Rep, Interested In or Result is filtered. Hours per Register " +
      "to Win = Hours Worked ÷ Register to Win.",
  },
  { term: "Demo Pay", body: DEMO_PAY_RULE },
  {
    term: "Total Revenue",
    body: "Net sales: original sale + change orders − cancellations, the same figure the Marketing Performance report uses.",
  },
  { term: "Sales and Average Sale", body: "A sale counts once it is sold and not cancelled. Average Sale = Total Revenue ÷ sales." },
  {
    term: "Funnel",
    body:
      "Each stage counts the leads that ever reached it (Set, Issued, Needs Assessment, Demo, Sale), so a lead's " +
      "current result can be earlier in the funnel than the furthest stage it reached.",
  },
  { term: "Dates", body: "Filtered by appointment date (Central time). Leads without an appointment use the date they were entered." },
  { term: "Refresh", body: `${REFRESH} Covers event leads since October 2023.` },
];

export const RECAP_DEFINITIONS: Definition[] = [
  {
    term: "Which leads, costs and hours count",
    body:
      "Everything LeadPerfection records under this event's subsource. When a subsource has more than one " +
      "event (a series), each lead, cost and hour goes to the event whose dates it falls in; the attribution " +
      "end date extends an event's window for late leads.",
  },
  { term: "Sales $ and Sales #", body: "Net sales (original + change orders − cancellations); a sale counts when sold and not cancelled. Closing % = Sales # ÷ Needs Assessments." },
  { term: "Team Commission", body: `${COMMISSION_RULE} Computed from the rates on the Rates page in effect on the event's start date; LeadPerfection's own Commission cost entries are not used, so commission is never counted twice.` },
  { term: "Manager Override", body: `${MANAGER_RULE} For a recap, the manager and rates in effect on the event's start date apply. It counts in Total Employee Costs.` },
  { term: "Demo Pay", body: `${DEMO_PAY_RULE} Shown for reference; it is not part of Total Marketing Spend.` },
  {
    term: "Booth costs",
    body:
      "From LeadPerfection Marketing Cost Detail: Booth / Sponsorship Fee (Space Rental), Event Supplies, and " +
      "Event Misc. (Misc. Venue). Register to Win Giveaway Prize and Marketing Expense are not included.",
  },
  {
    term: "Employee hours and staffing cost",
    body:
      "Hours from LeadPerfection Event Hours; Staffing Cost is the Hourly Pay total entered in Marketing Cost " +
      "Detail. Hours logged under a name that matches no LeadPerfection promoter are flagged so the name can be fixed in LP.",
  },
  { term: "Total Marketing Spend and Cost of Marketing %", body: "Booth costs + staffing cost + team commission + manager override. Cost of Marketing % = spend ÷ Sales $." },
  {
    term: "As saved vs Live",
    body:
      "Saving the assumptions also freezes a copy of every number as it was at that moment (\"As saved\"). " +
      "\"Live\" is recomputed from LeadPerfection on every refresh; the difference shows late leads, cancellations and cost entries since the save.",
  },
  { term: "Refresh", body: REFRESH },
];
